/**
 * La pestaña Biblia: la primera fase (Rais, 10-oct-2026: «lo primero que hay que hacer es la Biblia»; «no
 * puedes tener un guion del escritor sin tener una Biblia»).
 *
 * Dice cuál es la biblia de este libro —la que el motor tiene delante al verificar cada escena—, la abre en
 * Word y, si todavía no hay, cuenta las dos maneras de tenerla: traer la que ya está escrita o hacerla hablando
 * con el coach en la pantalla grande. No lee el documento de Word ni le pregunta nada: la carpeta se la da el
 * panel. Mirar no gasta; escribir al coach, sí, y se dice.
 */
import { useCallback, useEffect, useState } from "react";
import * as api from "./guionApi";
import { diaYHora, type Biblia as Estado } from "./guionLogica";
import { reportPanelError } from "./companion";

export type PuenteDeLaBiblia = Pick<typeof api, "bibliaDe" | "abrirFuente" | "abrirConversacion">;
const DE_VERDAD: PuenteDeLaBiblia = { bibliaDe: api.bibliaDe, abrirFuente: api.abrirFuente, abrirConversacion: api.abrirConversacion };

type Dicho = { tono: "bien" | "aviso" | "error"; texto: string };
const COLOR = { bien: "var(--hi-bien)", aviso: "var(--hi-aviso)", error: "var(--hi-mal)" } as const;
const caja = { border: "1px solid var(--hi-raya)", borderRadius: 10, padding: "10px 12px", background: "var(--hi-papel)", fontSize: 13, lineHeight: 1.45, marginBottom: 10 } as const;
const rotulo = { fontSize: 11, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--hi-oficio)", marginBottom: 2 } as const;
const nota = { fontSize: 12, color: "var(--hi-tenue)", margin: "6px 0" } as const;
const boton = { background: "var(--hi-humano)", color: "var(--hi-sobre-acento)", fontWeight: 800, border: 0, borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: "pointer" } as const;
const enlace = { background: "none", border: 0, padding: 0, color: "var(--hi-humano)", fontSize: 12, cursor: "pointer", textDecoration: "underline" } as const;

export default function Biblia({ carpeta, doc = null, alMirar, alIr, puente = DE_VERDAD }: {
  /** La carpeta del documento abierto («» si Word todavía no la dice o el documento está en la nube). */
  carpeta: string;
  /** El documento abierto: con él el motor sabe de qué libro se trata si en la carpeta hay más de uno. */
  doc?: string | null;
  /** Volver a preguntar dónde está el documento: solo al pulsar. */
  alMirar: () => void;
  alIr: (p: "guion") => void;
  puente?: PuenteDeLaBiblia;
}) {
  const { bibliaDe, abrirFuente, abrirConversacion } = puente;
  const [estado, setEstado] = useState<{ fase: "mirando" } | { fase: "error"; texto: string } | { fase: "lista"; biblia: Estado }>({ fase: "mirando" });
  const [abierta, setAbierta] = useState<Dicho | null>(null);
  const [coach, setCoach] = useState<Dicho | null>(null);

  const mirar = useCallback(async () => {
    if (!carpeta) return;
    setEstado({ fase: "mirando" });
    try { setEstado({ fase: "lista", biblia: await bibliaDe(carpeta, doc) }); }
    catch (e) {
      const err = e as { message?: string; codigo?: string };
      setEstado({ fase: "error", texto: String(err?.message ?? e) });
      if (!err?.codigo) reportPanelError("biblia:mirar", e);
    }
  }, [carpeta, doc, bibliaDe]);
  useEffect(() => { void mirar(); }, [mirar]);

  const abrir = async () => {
    try {
      const r = await abrirFuente(carpeta, doc, "biblia");
      setAbierta(r.abierto ? { tono: "bien", texto: `«${r.nombre}» se está abriendo en Word.` } : { tono: "aviso", texto: `No he podido abrirla desde aquí. Está en la carpeta del libro: ${r.nombre}.` });
    } catch (e) { setAbierta({ tono: "error", texto: String((e as Error)?.message ?? e) }); }
  };
  const hablar = async () => {
    try {
      const r = await abrirConversacion(carpeta, doc);
      setCoach(r.abierta ? { tono: "bien", texto: "La conversación está abierta en el navegador." } : { tono: "aviso", texto: `No he podido abrir el navegador. Ábrelo tú y ve a: ${r.url}` });
    } catch (e) { setCoach({ tono: "error", texto: String((e as Error)?.message ?? e) }); }
  };
  const conElCoach = (
    <>
      <button onClick={() => void hablar()} style={enlace}>Abrir la conversación con el coach</button>
      <div style={{ fontSize: 11, color: "var(--hi-apagado)", marginTop: 2 }}>Se abre en el navegador. Abrirla no gasta; cada mensaje que le escribas, sí, de tu llave de OpenRouter.</div>
      {coach && <div role={coach.tono === "error" ? "alert" : "status"} style={{ fontSize: 12, color: COLOR[coach.tono], marginTop: 4, overflowWrap: "anywhere" }}>{coach.texto}</div>}
    </>
  );

  if (!carpeta) return (
    <div>
      <p style={nota}>Word todavía no me dice dónde está guardado este documento, y la biblia se busca en la carpeta del libro. Si ya está guardado en este ordenador, pulsa aquí.</p>
      <button onClick={alMirar} style={boton}>Volver a mirar</button>
    </div>
  );
  if (estado.fase === "mirando") return <p style={nota}>Mirando la carpeta del libro…</p>;
  if (estado.fase === "error") return <div style={{ ...caja, borderColor: "var(--hi-mal-raya)", color: "var(--hi-mal-suave)" }}>{estado.texto} <button onClick={() => void mirar()} style={enlace}>Volver a mirar</button></div>;

  const b = estado.biblia;
  if (b.hay) return (
    <div>
      <div style={caja}>
        <div style={rotulo}>La biblia de este libro</div>
        <div style={{ fontSize: 15, fontWeight: 800, overflowWrap: "anywhere" }}>«{b.nombre}»</div>
        <div style={{ fontSize: 12, color: "var(--hi-tenue)", marginTop: 2 }}>
          {diaYHora(b.cuando) ? `Guardada el ${diaYHora(b.cuando)}. ` : ""}Es la que se tiene delante al verificar cada escena.
        </div>
        <button onClick={() => void abrir()} style={{ ...boton, marginTop: 8 }}>Abrir la biblia en Word</button>
        {abierta && <div role={abierta.tono === "error" ? "alert" : "status"} style={{ fontSize: 12, color: COLOR[abierta.tono], marginTop: 6, overflowWrap: "anywhere" }}>{abierta.texto}</div>}
        {b.otras.length > 0 && (
          <div style={{ fontSize: 11, color: "var(--hi-apagado)", marginTop: 8 }}>
            En la carpeta hay {b.otras.length === 1 ? "otra versión" : `otras ${b.otras.length} versiones`}. Se usa la de número más alto.
          </div>
        )}
      </div>
      <div style={caja}>
        <div style={rotulo}>Para cambiarla</div>
        <p style={{ ...nota, marginTop: 0 }}>Guarda la nueva en la carpeta del libro, con «biblia» en el nombre y un número mayor al final. O trabájala con tu coach y, cuando te valga lo que te dé, pulsa «Guardar como biblia» debajo de su respuesta.</p>
        {conElCoach}
      </div>
      <button onClick={() => alIr("guion")} style={enlace}>Siguiente: el guion ›</button>
    </div>
  );
  if (b.varias?.length) return (
    <div style={{ ...caja, borderColor: "var(--hi-aviso-raya)" }}>
      <b>En esta carpeta hay biblias de más de un libro y no sé cuál es la de este.</b>
      <ul style={{ margin: "6px 0", paddingLeft: 18, fontSize: 12, overflowWrap: "anywhere" }}>{b.varias.map((n) => <li key={n}>{n}</li>)}</ul>
      <p style={nota}>Cada libro, en su carpeta: así no se comprueba una novela contra la biblia de otra.</p>
      <button onClick={() => void mirar()} style={enlace}>Volver a mirar</button>
    </div>
  );
  return (
    <div>
      <div style={caja}>
        <b>Este libro todavía no tiene biblia.</b>
        <p style={nota}>Es lo primero: sin biblia no se puede hacer el guion. En ella está quién es quién, dónde y cuándo pasa, las reglas del mundo, la voz y la escaleta: todo lo necesario para entrar después en el detalle de cada escena. Contra ella se comprueba lo que escribes.</p>
      </div>
      <div style={caja}>
        <div style={rotulo}>Si ya la tienes escrita</div>
        <p style={{ ...nota, marginTop: 0 }}>Pon su Word en la carpeta del libro, con «biblia» en el nombre, y pulsa aquí.</p>
        <button onClick={() => void mirar()} style={boton}>Volver a mirar</button>
      </div>
      <div style={caja}>
        <div style={rotulo}>Si empiezas de cero</div>
        <p style={{ ...nota, marginTop: 0 }}>Hazla hablando con tu coach, en la pantalla grande, que ahí hay sitio para leer. Pídele que incluya la escaleta. Cuando te dé una versión que te valga, pulsa «Guardar como biblia» debajo de su respuesta y se queda como Word con tu libro.</p>
        {conElCoach}
      </div>
    </div>
  );
}
